import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import {
  CategoryHashes,
  CategoryLookup,
} from "../../category-builder/category-builder.models";
import { ContextService } from "../../context/state/context.service";
import { EaredDoveService } from "../../eared-dove/eared-dove.service";
import { GroupDetails, ModelType } from "../../models/state/model";
import { fieldIcon } from "../../shared/models/icons.model";
import { sentimentIcon } from "../../shared/models/icons.model";

@Component({
  selector: "app-verbatim-sentences",
  templateUrl: "./verbatim-sentences.component.html",
  styleUrls: ["./verbatim-sentences.component.scss"],
  standalone: false,
})
export class VerbatimSentencesComponent implements OnInit, OnDestroy {
  @Input() sentences: any[];
  @Input() hashes: CategoryHashes;
  @Input() lookup: CategoryLookup;
  @Input() verbatimId: string;
  fieldIcon = fieldIcon;
  destroyed$ = new Subject<boolean>();

  sentimentIcon = sentimentIcon;
  showDetails: boolean = false;
  detailsLoaded: boolean = false;
  groupsDetails: GroupDetails[] = [];

  constructor(
    private contextService: ContextService,
    private earedDoveService: EaredDoveService,
  ) {}

  ngOnInit() {
    this.normalizePredictionFields();

    this.contextService.allContext$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((context) => {
        this.setSetimentContextValues(context?.s);
        if (!context?.c?.version?.uuid) return;
        this.earedDoveService
          .getHashes(
            context?.c?.version?.version_data,
            context?.c?.version?.options,
            context?.s?.version?.uuid,
            context?.t?.version?.uuid,
            context?.e?.version?.uuid,
            context?.i?.version?.uuid,
          )
          .pipe(takeUntil(this.destroyed$))
          .subscribe((hashes: CategoryHashes) => (this.hashes = hashes));
      });

    if (!this.lookup) {
      this.contextService.categoryLookup$
        .pipe(takeUntil(this.destroyed$))
        .subscribe((lookup: CategoryLookup) => (this.lookup = lookup));
    }
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  setSetimentContextValues(sentimentContext) {
    const modelType = sentimentContext?.model?.model_type;
    const modelVersionId = sentimentContext?.version?.uuid;

    this.sentences.forEach((sentence) => {
      const sentimentPredictions = Array.isArray(sentence?.sentiment)
        ? sentence.sentiment
        : [];
      const selectedSentiment =
        sentimentPredictions.find((s) => s?.version_id === modelVersionId) ||
        sentimentPredictions[0];

      sentence["sentiment_context_value"] = selectedSentiment?.predict;

      if (modelType === ModelType.LEXICON_SENTIMENT) {
        this.showDetails = true;
        this.earedDoveService
          .getSentimentDetails(modelVersionId, sentence.sentence)
          .subscribe((sentimentDetails) => {
            this.groupsDetails = sentimentDetails[0].details;
            this.detailsLoaded = true;
          });
      }
    });
  }

  hasPredictionData(sentence: any): boolean {
    return ["topic", "emotion", "fluff", "intent", "effort"].some(
      (key) => Array.isArray(sentence?.[key]) && sentence[key].length > 0,
    );
  }

  private normalizePredictionFields(): void {
    if (!Array.isArray(this.sentences)) {
      return;
    }

    this.sentences.forEach((sentence) => {
      sentence.topic = this.getArrayValue(sentence, ["topic", "topics"]);
      sentence.emotion = this.getArrayValue(sentence, ["emotion", "emotions"]);
      sentence.fluff = this.getArrayValue(sentence, [
        "fluff",
        "fluff_statements",
      ]);
      sentence.intent = this.getArrayValue(sentence, ["intent", "intents"]);
      sentence.effort = this.getArrayValue(sentence, [
        "effort",
        "effort_statements",
      ]);
    });
  }

  private getArrayValue(source: any, keys: string[]): any[] {
    for (const key of keys) {
      if (Array.isArray(source?.[key])) {
        return source[key];
      }
    }

    return [];
  }

  joinWords(groupDetails: GroupDetails) {
    return groupDetails.words
      .map((wordDetails) => wordDetails.raw_text)
      .join(" ");
  }

  isIdiom(groupDetails: GroupDetails) {
    return groupDetails.words
      .map((wordDetails) => wordDetails.type == "idiom")
      .some((v) => v === true);
  }
}
